import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getTrainingRecommendation, getEnterpriseTrainingRecommendation, readableApiError } from '../../api/dashboard';
import './dashboard.css';

// V5: 优先级标签中文化 (DB / API 保留 P0/P1/P2 code, UI 文案不出现三字母)
const PRIORITY_LABEL = {
  P0: '立即开展',
  P1: '本季跟进',
  P2: '后续观察',
};
// 扁平色（去掉描边色条；只留 badge 的 bg/fg）
const PRIORITY_COLOR = {
  P0: { bg: '#fbeaea', fg: '#b91c1c' },
  P1: { bg: '#fbf1e3', fg: '#b45309' },
  P2: { bg: '#e3f3f1', fg: '#0f766e' },
};
const PRIORITY_ORDER = { P0: 0, P1: 1, P2: 2 };

// 行动路线图 3 步 (按 affected_count desc 取前 3)。配色走 dashboard.css 的 dash-step--sN（扁平，无渐变）。
const STEP_META = [
  { cls: 'dash-step--s1', label: '立即开展' },
  { cls: 'dash-step--s2', label: '紧随其后' },
  { cls: 'dash-step--s3', label: '本季跟进' },
];

const L_GRADE_COLOR = {
  L1: '#94a3b8',
  L2: '#0ea5e9',
  L3: '#0f766e',
  L4: '#15803d',
};
const L_GRADE_LABEL = {
  L1: 'L1 知晓尝试级',
  L2: 'L2 基础应用级',
  L3: 'L3 协同优化级',
  L4: 'L4 创新引领级',
};


// scope-aware（class | enterprise）。
export default function ClassRecommendationPage({ scope = 'class' }) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const isEnt = scope === 'enterprise';
  const basePath = isEnt ? `/dashboard/enterprises/${id}` : `/dashboard/classes/${id}`;

  const load = useCallback((opts = {}) => {
    if (opts.refresh) setRefreshing(true);
    else setData(null);
    setError(null);
    return (isEnt ? getEnterpriseTrainingRecommendation(id, opts) : getTrainingRecommendation(id, opts))
      .then((d) => setData(d))
      .catch((err) => setError(readableApiError(err)))
      .finally(() => setRefreshing(false));
  }, [id, isEnt]);

  useEffect(() => { load(); }, [load]);

  const codeToName = useMemo(() => {
    const m = {};
    (data?.team_weakness || []).forEach((w) => { m[w.dim_code] = w.dim_name; });
    return m;
  }, [data]);

  if (error) return <div className="aiq-dash dash-page"><div className="dash-card dash-card--error">{error}</div></div>;
  if (!data) return <div className="aiq-dash dash-page"><div className="dash-card">加载中...</div></div>;

  const aiMeta = data.ai_meta || {};
  const aiTruncated = aiMeta.error === 'json_truncated';
  const aiError = !!aiMeta.error && !aiTruncated;
  const skipped = aiMeta.skipped === 'no_team_weakness';
  const recs = [...(data.recommendations || [])].sort(
    (a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9)
  );

  // 行动路线图: 按 affected_count desc 取 top 3 (Python 排序逻辑前端复用)
  const roadmap = [...(data.team_weakness || [])]
    .sort((a, b) => (b.affected_count || 0) - (a.affected_count || 0))
    .slice(0, 3);

  return (
    <div className="aiq-dash dash-page">
      <div className="dash-head">
        <h1 className="dash-h1">培训建议</h1>
        <p className="dash-sub">
          AI 基于团队短板（维度均分 &lt; 65 分）+ 团队画像 + 行业上下文生成「诊断 + 方向」风格的提升建议。不推荐具体课程 / 课时。
        </p>
      </div>

      <div className="dash-toolbar">
        <Link to={basePath} className="dash-back">← 返回总览</Link>
        <button onClick={() => load({ refresh: true })} disabled={refreshing}
          className="dash-btn dash-btn--sec" style={{ cursor: refreshing ? 'wait' : 'pointer' }}>
          {refreshing ? '重新生成中...' : '重新生成（跳过缓存）'}
        </button>
      </div>

      {/* 团队画像板块 (V5 新增) */}
      <TeamPortraitSection data={data} />

      {/* 短板列表 (加列) */}
      <div className="dash-card">
        <div className="dash-sec"><h2>团队短板</h2></div>
        {(data.team_weakness || []).length === 0 ? (
          <div className="dash-mut">
            团队各维度均分 ≥ 65 分，暂无明显短板。
          </div>
        ) : (
          <table className="dash-table">
            <thead>
              <tr>
                <th>维度</th>
                <th className="dash-num-cell">团队均分</th>
                <th className="dash-num-cell">影响人数</th>
                <th className="dash-num-cell">距阈值差</th>
              </tr>
            </thead>
            <tbody>
              {data.team_weakness.map((w) => (
                <tr key={w.dim_code}>
                  <td>{w.dim_name}</td>
                  <td className="dash-num-cell" style={{ fontWeight: 700, color: 'var(--da-bad)' }}>
                    {w.avg_score}
                  </td>
                  <td className="dash-num-cell" style={{ fontWeight: 600 }}>
                    {w.affected_count} 人
                  </td>
                  <td className="dash-num-cell" style={{ color: 'var(--da-bad)' }}>
                    {(w.avg_score - 65).toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 建议列表 / 空态 / AI 失败态 */}
      {skipped ? (
        <div className="dash-card">
          <div className="dash-mut">
            团队整体表现稳定，未触发 AI 生成。可优先关注个体差异（见
            <Link to={`${basePath}/distribution`} className="dash-link" style={{ marginLeft: 4 }}>差异分布</Link>）。
          </div>
        </div>
      ) : aiTruncated ? (
        <div className="dash-card" style={{ borderColor: '#f0dcc0', background: 'var(--da-warn-bg)' }}>
          <div style={{ color: '#9a3412', fontWeight: 700, marginBottom: 6 }}>AI 输出截断</div>
          <div className="dash-mut" style={{ color: '#7c2d12' }}>
            内容超过模型单次输出上限（5 分钟内自动重试缓存）。可点击右上角"重新生成"立即重试。
          </div>
        </div>
      ) : aiError ? (
        <div className="dash-card" style={{ borderColor: '#f3c9c9', background: 'var(--da-bad-bg)' }}>
          <div style={{ color: '#991b1b', fontWeight: 700, marginBottom: 6 }}>AI 生成失败</div>
          <div className="dash-mut" style={{ color: '#7f1d1d' }}>
            {aiMeta.error}（5 分钟内自动重试缓存）。可点击右上角"重新生成"立即重试。
          </div>
        </div>
      ) : recs.length === 0 ? (
        <div className="dash-card">
          <div className="dash-mut">AI 暂未给出可用建议。可点击"重新生成"重试。</div>
        </div>
      ) : (
        <>
          {/* 提升方向卡 (7 字段诊断风格) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {recs.map((r, idx) => (
              <ImprovementCard key={idx} rec={r} codeToName={codeToName} />
            ))}
          </div>

          {/* 行动路线图 */}
          {roadmap.length > 0 && (
            <div className="dash-card">
              <div className="dash-sec"><h2>行动路线图</h2><span className="dash-meta">按影响人数排序</span></div>
              <div className="dash-road">
                {roadmap.map((w, idx) => (
                  <div key={w.dim_code} className={`dash-step ${STEP_META[idx].cls}`}>
                    <span className="dash-chip"><span className="dash-num">{idx + 1}</span>{STEP_META[idx].label}</span>
                    <div className="dash-step__dn">{w.dim_name}</div>
                    <div className="dash-step__mt">影响 <b>{w.affected_count}</b> 人 · 团队均分 <b>{w.avg_score}</b></div>
                    <div className="dash-step__sub">距阈值 {(w.avg_score - 65).toFixed(1)} 分</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
}


function TeamPortraitSection({ data }) {
  const layerDist = data.layer_distribution || [];
  const traitDist = data.trait_distribution || [];
  const positionTop5 = data.position_top5 || [];
  const totalLayered = layerDist.reduce((s, x) => s + (x.count || 0), 0);

  return (
    <div className="dash-card">
      <div className="dash-sec"><h2>团队画像</h2><span className="dash-meta">AI 生成建议时同步引用</span></div>

      {/* class_headline_tag + class_summary */}
      {(data.class_headline_tag || data.class_summary) && (
        <div className="dash-callout" style={{ marginBottom: 16 }}>
          {data.class_headline_tag && (
            <div className="dash-callout__tag">{data.class_headline_tag}</div>
          )}
          {data.class_summary && (
            <div className="dash-callout__sum">{data.class_summary}</div>
          )}
        </div>
      )}

      <div className="dash-portrait-grid" style={{ marginBottom: 16 }}>
        {/* L1-L4 堆叠条 */}
        <div>
          <div className="dash-sublabel">
            L1-L4 等级分布（{totalLayered} 名已完成学员）
          </div>
          <div className="dash-stack">
            {layerDist.map((l) => (
              <div key={l.l_grade} className="dash-stack__seg" style={{
                flex: l.count || 0.001,
                background: L_GRADE_COLOR[l.l_grade],
                minWidth: l.count ? 24 : 0,
              }}>
                {l.count > 0 && `${l.l_grade} · ${l.count}`}
              </div>
            ))}
          </div>
          <div className="dash-legend">
            {layerDist.map((l) => (
              <div key={l.l_grade} className="dash-legend__item">
                <div className="dash-legend__sw" style={{ background: L_GRADE_COLOR[l.l_grade] }} />
                <span>{L_GRADE_LABEL[l.l_grade]} · {l.count} 人 · {l.pct}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* 3 素质 */}
        <div>
          <div className="dash-sublabel">
            3 素质均分（满分 4 分）
          </div>
          {traitDist.map((t) => (
            <div key={t.trait_code} className="dash-trait">
              <div className="dash-trait__nm">{t.trait_name}</div>
              <div className="dash-trait__tk">
                {t.avg != null && (
                  <div className="dash-trait__fl" style={{ width: `${(t.avg / 4) * 100}%` }} />
                )}
              </div>
              <div className="dash-trait__vl">
                {t.avg != null ? t.avg.toFixed(2) : '—'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 职位 top 5 */}
      {positionTop5.length > 0 && (
        <div>
          <div className="dash-sublabel">
            学员职位 top 5
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {positionTop5.map((p) => (
              <div key={p.position} className="dash-pill">
                {p.position} · <b>{p.count}</b>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function ImprovementCard({ rec, codeToName }) {
  const colors = PRIORITY_COLOR[rec.priority] || PRIORITY_COLOR.P1;
  const dimNames = (rec.target_dims || []).map((c) => codeToName[c] || c);
  const linkNames = (rec.cross_dim_links || []);

  return (
    <div className="dash-imp">
      <div className="dash-imp__hd">
        <span className="dash-badge" style={{ background: colors.bg, color: colors.fg }}>
          {PRIORITY_LABEL[rec.priority] || rec.priority}
        </span>
        <h3 className="dash-imp__title">{rec.title}</h3>
      </div>
      <div className="dash-mut">
        影响人数 <b style={{ color: 'var(--da-ink)' }}>{rec.affected_count}</b>
        　·　覆盖维度: <b style={{ color: 'var(--da-ink)' }}>{dimNames.length ? dimNames.join('、') : '—'}</b>
      </div>

      {rec.why_priority && (
        <FieldBlock label="为什么是这个优先级">{rec.why_priority}</FieldBlock>
      )}
      {rec.improvement_directions?.length > 0 && (
        <FieldBlock label="具体应提升的能力子项">
          <ul>
            {rec.improvement_directions.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </FieldBlock>
      )}
      {rec.observable_behaviors && (
        <FieldBlock label="提升后可观察的行为">{rec.observable_behaviors}</FieldBlock>
      )}
      {rec.if_skipped_risks && (
        <FieldBlock label="不做的业务风险">{rec.if_skipped_risks}</FieldBlock>
      )}
      {linkNames.length > 0 && (
        <FieldBlock label="联动维度">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            {linkNames.map((c) => (
              <span key={c} className="dash-pill">↗ {codeToName[c] || c}</span>
            ))}
          </div>
        </FieldBlock>
      )}
    </div>
  );
}


function FieldBlock({ label, children }) {
  return (
    <div className="dash-field">
      <div className="dash-field__label">{label}</div>
      <div className="dash-field__body">{children}</div>
    </div>
  );
}
